import { useState, useMemo, useEffect } from "react";
import api from "../lib/api";
import { Button } from "./ui/button";
import { ChevronLeft, ChevronRight, Calendar as CalendarIcon, CalendarDays, Layers, Clock, MapPin, User, Users } from "lucide-react";

const DAYS_FR = ["Lundi", "Mardi", "Mercredi", "Jeudi", "Vendredi", "Samedi", "Dimanche"];
const MONTHS = ["Janvier", "Février", "Mars", "Avril", "Mai", "Juin", "Juillet", "Août", "Septembre", "Octobre", "Novembre", "Décembre"];
const ML = { arabe: "Arabe", coran: "Coran", education_musulmane: "Éd. Musulmane" };
const MAT_COLORS = {
  arabe: { bg: "bg-[#E0E7FF]", text: "text-[#1E3A8A]", border: "border-[#1E3A8A]/30", dot: "bg-[#1E3A8A]" },
  coran: { bg: "bg-[#FBE8DC]", text: "text-[#C86444]", border: "border-[#C86444]/30", dot: "bg-[#C86444]" },
  education_musulmane: { bg: "bg-[#E0F2E9]", text: "text-[#1E7A4D]", border: "border-[#1E7A4D]/30", dot: "bg-[#1E7A4D]" },
};

// Type d'événement (meeting.kind) — couleurs visuelles distinctes
const KIND_STYLES = {
  reunion: { bg: "bg-[#FBE8DC]", text: "text-[#C86444]", border: "border-[#C86444]/30", label: "Réunion" },
  cours_supplementaire: { bg: "bg-[#E0F2E9]", text: "text-[#1E7A4D]", border: "border-[#1E7A4D]/30", label: "Cours suppl." },
  cours_annule: { bg: "bg-[#FBE5E4]", text: "text-[#B0413E]", border: "border-[#B0413E]/30", label: "Annulé" },
  autre: { bg: "bg-[#E0E7FF]", text: "text-[#1E3A8A]", border: "border-[#1E3A8A]/30", label: "Événement" },
};

function meetingStyle(m) {
  return KIND_STYLES[m.kind || "reunion"] || KIND_STYLES.reunion;
}
function meetingLabel(m) {
  if (m.kind === "autre" && m.custom_label) return m.custom_label;
  return (KIND_STYLES[m.kind || "reunion"] || KIND_STYLES.reunion).label;
}

function startOfWeek(d) {
  const x = new Date(d); x.setHours(0, 0, 0, 0);
  const day = (x.getDay() + 6) % 7; // Mon=0..Sun=6
  x.setDate(x.getDate() - day);
  return x;
}
function addDays(d, n) { const x = new Date(d); x.setDate(x.getDate() + n); return x; }
function fmtDate(d) {
  const yy = d.getFullYear(); const mm = String(d.getMonth() + 1).padStart(2, "0"); const dd = String(d.getDate()).padStart(2, "0");
  return `${yy}-${mm}-${dd}`;
}
function isVacation(date, vacations) {
  const ds = fmtDate(date);
  return vacations.find((v) => v.start_date <= ds && ds <= v.end_date);
}
function normalizeDay(day) {
  if (!day) return "";
  const d = day.trim().toLowerCase();
  return DAYS_FR.find((x) => x.toLowerCase() === d) || "";
}
function dayIndexOfCourse(course) {
  const dayName = normalizeDay(course.schedule_day);
  return DAYS_FR.indexOf(dayName); // -1 if not found
}
function startMinutes(time) {
  const m = (time || "").match(/(\d{1,2}):(\d{2})/);
  if (!m) return 9999;
  return parseInt(m[1]) * 60 + parseInt(m[2]);
}

// Normalise un schedule_time pour comparaison (espaces + tirets unifiés)
function normalizeTime(t) {
  if (!t) return "";
  return String(t)
    .replace(/[\u2010-\u2015\u2212]/g, "-")  // tirets unicode → "-"
    .replace(/\s+/g, " ")                     // espaces multiples → 1 espace
    .replace(/\s*-\s*/g, "-")                  // " - " → "-"
    .trim()
    .toLowerCase();
}

export default function CalendarView({ courses, vacations = [], meetings = [], groupName = () => "", initialMode = "week" }) {
  const [mode, setMode] = useState(initialMode);
  const [cursor, setCursor] = useState(new Date());
  const [yearBounds, setYearBounds] = useState(null);

  useEffect(() => {
    api.get("/school-years/active")
      .then((r) => {
        if (r.data?.start_date && r.data?.end_date) {
          setYearBounds({ start: r.data.start_date, end: r.data.end_date, label: r.data.label });
        }
      })
      .catch(() => {});
  }, []);

  // Un jour est "dans l'année scolaire" si compris entre start_date et end_date de l'année active
  const inYear = useMemo(() => {
    if (!yearBounds) return () => true;
    return (d) => {
      const s = fmtDate(d);
      return s >= yearBounds.start && s <= yearBounds.end;
    };
  }, [yearBounds]);

  const weekStart = useMemo(() => startOfWeek(cursor), [cursor]);
  const days = useMemo(() => Array.from({ length: 7 }, (_, i) => addDays(weekStart, i)), [weekStart]);

  // Group courses by weekday index
  const coursesByDay = useMemo(() => {
    const map = Array.from({ length: 7 }, () => []);
    for (const c of courses) {
      const idx = dayIndexOfCourse(c);
      if (idx >= 0) map[idx].push(c);
    }
    map.forEach((arr) => arr.sort((a, b) => {
      const dt = startMinutes(a.schedule_time) - startMinutes(b.schedule_time);
      if (dt !== 0) return dt;
      return (a.order || 99) - (b.order || 99);
    }));
    return map;
  }, [courses]);

  const navWeek = (delta) => setCursor((c) => addDays(c, delta * 7));
  const navMonth = (delta) => {
    const x = new Date(cursor); x.setMonth(x.getMonth() + delta); setCursor(x);
  };
  const today = () => setCursor(new Date());

  const headerLabel = mode === "week"
    ? `Week-end du ${addDays(weekStart, 5).getDate()} ${MONTHS[addDays(weekStart, 5).getMonth()].toLowerCase()} au ${addDays(weekStart, 6).getDate()} ${MONTHS[addDays(weekStart, 6).getMonth()].toLowerCase()} ${addDays(weekStart, 6).getFullYear()}`
    : `${MONTHS[cursor.getMonth()]} ${cursor.getFullYear()}`;

  return (
    <div data-testid="calendar-view">
      {/* Toolbar */}
      <div className="bg-white border border-[#EAE6DF] rounded-2xl p-4 mb-5 flex flex-wrap items-center justify-between gap-3">
        <div className="flex items-center gap-2">
          <Button size="sm" variant="ghost" className="rounded-full" onClick={() => mode === "week" ? navWeek(-1) : navMonth(-1)} data-testid="cal-prev">
            <ChevronLeft className="w-4 h-4" />
          </Button>
          <Button size="sm" variant="outline" className="rounded-full border-[#1E3A8A]/30 text-[#1E3A8A]" onClick={today} data-testid="cal-today">Aujourd'hui</Button>
          <Button size="sm" variant="ghost" className="rounded-full" onClick={() => mode === "week" ? navWeek(1) : navMonth(1)} data-testid="cal-next">
            <ChevronRight className="w-4 h-4" />
          </Button>
          <div className="ml-3 font-[Outfit] font-semibold text-[#1A2547]">{headerLabel}</div>
        </div>
        <div className="flex items-center gap-1 bg-[#F4F1EB] rounded-full p-1">
          <button onClick={() => setMode("week")} className={`px-3 py-1 text-xs rounded-full ${mode === "week" ? "bg-white shadow-sm font-medium" : "text-[#5c6374]"}`} data-testid="mode-week">
            <CalendarDays className="w-3.5 h-3.5 inline mr-1" /> Semaine
          </button>
          <button onClick={() => setMode("month")} className={`px-3 py-1 text-xs rounded-full ${mode === "month" ? "bg-white shadow-sm font-medium" : "text-[#5c6374]"}`} data-testid="mode-month">
            <CalendarIcon className="w-3.5 h-3.5 inline mr-1" /> Mois
          </button>
        </div>
      </div>

      {/* Legend + vacation strip */}
      <div className="flex flex-wrap gap-2 mb-4 text-xs">
        {Object.entries(ML).map(([k, label]) => (
          <span key={k} className={`inline-flex items-center gap-1.5 px-2.5 py-0.5 rounded-full ${MAT_COLORS[k].bg} ${MAT_COLORS[k].text} border ${MAT_COLORS[k].border}`}>
            <span className={`w-1.5 h-1.5 rounded-full ${MAT_COLORS[k].dot}`} />{label}
          </span>
        ))}
        <span className="inline-flex items-center gap-1.5 px-2.5 py-0.5 rounded-full bg-[#F4F1EB] text-[#8a92a3] border border-[#EAE6DF]">
          <span className="w-1.5 h-1.5 rounded-full bg-[#a8aebd]" />Vacances
        </span>
      </div>

      {yearBounds && (
        <div className="mb-4 text-xs text-[#8a92a3]">
          Année scolaire {yearBounds.label} : cours affichés du {new Date(yearBounds.start + "T00:00:00").toLocaleDateString("fr-FR")} au {new Date(yearBounds.end + "T00:00:00").toLocaleDateString("fr-FR")}
        </div>
      )}

      {mode === "week" ? (
        <WeekGrid days={days} coursesByDay={coursesByDay} vacations={vacations} meetings={meetings} groupName={groupName} inYear={inYear} yearBounds={yearBounds} />
      ) : (
        <MonthGrid cursor={cursor} coursesByDay={coursesByDay} vacations={vacations} meetings={meetings} inYear={inYear} />
      )}
    </div>
  );
}

function DayHeader({ d, i, vacations }) {
  const vac = isVacation(d, vacations);
  const isToday = fmtDate(d) === fmtDate(new Date());
  return (
    <div className={`px-3 py-2.5 border-b border-l border-white/15 ${isToday ? "bg-[#C86444]" : "bg-[#1E3A8A]"}`} data-testid={`day-header-${fmtDate(d)}`}>
      <div className="font-[Outfit] font-semibold text-sm text-white">{DAYS_FR[i]}</div>
      <div className="text-[10px] uppercase tracking-[0.18em] text-white/70">
        {d.getDate()} {MONTHS[d.getMonth()].slice(0, 4).toLowerCase()}
        {isToday && " · Aujourd'hui"}
        {vac && " · Vacances"}
      </div>
    </div>
  );
}

function WeekMeetingCard({ m }) {
  const ms = meetingStyle(m);
  return (
    <div className={`rounded-xl p-2.5 border ${ms.border} ${ms.bg}`} data-testid={`week-meeting-${m.id}`}>
      <div className={`text-[10px] font-semibold uppercase tracking-wider ${ms.text}`}>{meetingLabel(m)}</div>
      <div className={`text-xs font-medium ${ms.text} flex items-center gap-1 mt-0.5`}>
        <Clock className="w-3 h-3" strokeWidth={2} />{m.start_time}–{m.end_time}
      </div>
      <div className="mt-1 text-sm font-[Outfit] font-semibold text-[#1A2547]">{m.title}</div>
      {m.location && (
        <div className="text-xs text-[#8a92a3] flex items-center gap-1"><MapPin className="w-3 h-3 shrink-0" strokeWidth={2} /><span className="truncate">{m.location}</span></div>
      )}
      {m.description && <div className="text-[10px] text-[#5c6374] mt-1 line-clamp-2">{m.description}</div>}
    </div>
  );
}

function WeekCourseCard({ c, cancelled, groupName }) {
  const colors = MAT_COLORS[c.matiere] || MAT_COLORS.arabe;
  const strike = cancelled ? "line-through" : "";
  const tooltip = [
    ML[c.matiere],
    c.group_name || groupName(c.group_id),
    c.teacher_name && c.teacher_name !== "À définir" ? c.teacher_name : null,
    c.schedule_time,
    cancelled ? "ANNULÉ" : null,
  ].filter(Boolean).join(" · ");
  return (
    <div
      className={`relative rounded-xl border p-2.5 ${cancelled ? "border-[#B0413E]/40 bg-[#FBE5E4]/60 opacity-70" : `${colors.border} ${colors.bg}`}`}
      data-testid={`week-course-${c.id}`}
      title={tooltip}
    >
      {cancelled && (
        <div className="absolute top-1 right-1 text-[8px] font-bold uppercase tracking-wider text-[#B0413E] bg-white/80 px-1 rounded">Annulé</div>
      )}
      <div className={`font-[Outfit] font-semibold text-[#1A2547] truncate text-sm ${strike}`}>
        {ML[c.matiere]}
      </div>
      {c.group_id && (
        <div className={`mt-0.5 text-xs text-[#5c6374] flex items-center gap-1 ${strike}`}>
          <Layers className="w-3 h-3 shrink-0" strokeWidth={2} />
          <span className="truncate">{c.group_name || groupName(c.group_id)}</span>
        </div>
      )}
      {c.location && c.location !== "À définir" && (
        <div className={`text-xs text-[#8a92a3] flex items-center gap-1 ${strike}`}><MapPin className="w-3 h-3 shrink-0" strokeWidth={2} /><span className="truncate">{c.location}</span></div>
      )}
      {c.teacher_name && c.teacher_name !== "À définir" && (
        <div className={`text-xs text-[#8a92a3] flex items-center gap-1 ${strike}`}><User className="w-3 h-3 shrink-0" strokeWidth={2} /><span className="truncate">{c.teacher_name}</span></div>
      )}
    </div>
  );
}

function MeetingsRow({ visible, meetings, gridCols }) {
  return (
    <div className="grid border-b border-[#EAE6DF]" style={gridCols} data-testid="meetings-row">
      <div className="px-2 py-3 bg-[#F4F1EB] text-[10px] font-semibold uppercase tracking-wider text-[#8a968f] flex items-center justify-center text-center">Événements</div>
      {visible.map(({ d }) => {
        const dayMeetings = meetings.filter((m) => m.date === fmtDate(d)).sort((a, b) => (a.start_time || "").localeCompare(b.start_time || ""));
        return (
          <div key={fmtDate(d)} className="p-2 border-l border-[#EAE6DF] space-y-2">
            {dayMeetings.length === 0 && <div className="text-xs text-[#d4d8e0] text-center py-2">—</div>}
            {dayMeetings.map((m) => <WeekMeetingCard key={m.id} m={m} />)}
          </div>
        );
      })}
    </div>
  );
}

function SlotCell({ d, dayCourses, slot, vacations, cancelledMap, groupName, inYear }) {
  if (!inYear(d)) {
    return (
      <div className="p-2 border-l border-[#EAE6DF] bg-[#F4F1EB]/60 flex items-center justify-center">
        <span className="text-[10px] text-[#a8aebd]">Hors période scolaire</span>
      </div>
    );
  }
  const vac = isVacation(d, vacations);
  if (vac) {
    return (
      <div className="p-2 border-l border-[#EAE6DF] bg-[#F4F1EB]/60 flex items-center justify-center">
        <span className="text-[10px] text-[#a8aebd]">Vacances · {vac.name}</span>
      </div>
    );
  }
  const cell = dayCourses.filter((c) => normalizeTime(c.schedule_time) === slot);
  const cancelledThisDay = cancelledMap[fmtDate(d)] || new Set();
  return (
    <div className="p-2 border-l border-[#EAE6DF]" data-testid={`slot-cell-${fmtDate(d)}-${slot}`}>
      {cell.length === 0 && <div className="text-xs text-[#d4d8e0] text-center py-4">—</div>}
      <div className={cell.length > 1 ? "grid grid-cols-1 sm:grid-cols-2 gap-1.5" : ""}>
        {cell.map((c) => (
          <WeekCourseCard key={c.id} c={c} cancelled={cancelledThisDay.has(c.id)} groupName={groupName} />
        ))}
      </div>
    </div>
  );
}

function WeekGrid({ days, coursesByDay, vacations, meetings, groupName, inYear, yearBounds }) {
  // Map des cours annulés : { "YYYY-MM-DD": Set(course_id) }
  const cancelledMap = {};
  for (const m of (meetings || [])) {
    if (m.kind === "cours_annule" && m.target_course_id && m.date) {
      if (!cancelledMap[m.date]) cancelledMap[m.date] = new Set();
      cancelledMap[m.date].add(m.target_course_id);
    }
  }
  // Cours uniquement le week-end : n'afficher que samedi (5) et dimanche (6),
  // plus un éventuel jour de semaine s'il contient un événement ou un cours exceptionnel.
  const visible = days
    .map((d, i) => ({ d, i }))
    .filter(({ d, i }) =>
      i >= 5 ||
      coursesByDay[i].length > 0 ||
      meetings.some((m) => m.date === fmtDate(d))
    );

  // Lignes = tranches horaires distinctes (normalisées), triées par heure de début
  const slotLabels = {};
  for (const { i } of visible) {
    for (const c of coursesByDay[i]) {
      const k = normalizeTime(c.schedule_time);
      if (!slotLabels[k]) slotLabels[k] = c.schedule_time || "Horaire non défini";
    }
  }
  const slots = Object.keys(slotLabels).sort((a, b) => startMinutes(a) - startMinutes(b));
  const hasMeetings = visible.some(({ d }) => meetings.some((m) => m.date === fmtDate(d)));
  const gridCols = { gridTemplateColumns: `92px repeat(${visible.length}, minmax(0, 1fr))` };

  const anyInYear = visible.some(({ d }) => inYear(d));
  if (!anyInYear && !hasMeetings) {
    return (
      <div className="bg-white border border-[#EAE6DF] rounded-2xl p-10 text-center text-[#5c6374]" data-testid="out-of-year-banner">
        Hors période de l'année scolaire active
        {yearBounds && <span className="block text-xs text-[#8a92a3] mt-1">Cours du {new Date(yearBounds.start + "T00:00:00").toLocaleDateString("fr-FR")} au {new Date(yearBounds.end + "T00:00:00").toLocaleDateString("fr-FR")}</span>}
      </div>
    );
  }

  if (slots.length === 0 && !hasMeetings) {
    return (
      <div className="bg-white border border-[#EAE6DF] rounded-2xl p-10 text-center text-[#5c6374]">
        Aucun cours ni événement ce week-end.
      </div>
    );
  }

  return (
    <div className="overflow-x-auto">
      <div className="min-w-[520px] bg-white border border-[#EAE6DF] rounded-2xl overflow-hidden" data-testid="week-timetable">
        <div className="grid" style={gridCols}>
          <div className="bg-[#1E3A8A] border-b border-[#1E3A8A]" />
          {visible.map(({ d, i }) => <DayHeader key={fmtDate(d)} d={d} i={i} vacations={vacations} />)}
        </div>

        {hasMeetings && <MeetingsRow visible={visible} meetings={meetings} gridCols={gridCols} />}

        {slots.map((slot, si) => (
          <div key={slot} className={`grid ${si < slots.length - 1 ? "border-b border-[#EAE6DF]" : ""}`} style={gridCols} data-testid={`slot-row-${slot}`}>
            <div className="px-2 py-3 bg-[#F4F1EB] flex flex-col items-center justify-center text-center gap-1">
              <Clock className="w-3.5 h-3.5 text-[#8a968f]" strokeWidth={2} />
              <span className="text-xs font-semibold text-[#1A2547] leading-tight">{slotLabels[slot]}</span>
            </div>
            {visible.map(({ d, i }) => (
              <SlotCell
                key={fmtDate(d)}
                d={d}
                dayCourses={coursesByDay[i]}
                slot={slot}
                vacations={vacations}
                cancelledMap={cancelledMap}
                groupName={groupName}
                inYear={inYear}
              />
            ))}
          </div>
        ))}
      </div>
    </div>
  );
}

function MonthGrid({ cursor, coursesByDay, vacations, meetings, inYear }) {
  const year = cursor.getFullYear();
  const month = cursor.getMonth();
  const lastDay = new Date(year, month + 1, 0);
  // Week-ends (samedi + dimanche) couvrant le mois
  const weekends = [];
  let sat = addDays(startOfWeek(new Date(year, month, 1)), 5);
  while (sat <= lastDay) {
    weekends.push([sat, addDays(sat, 1)]);
    sat = addDays(sat, 7);
  }

  return (
    <div className="bg-white border border-[#EAE6DF] rounded-2xl overflow-hidden">
      <div className="grid grid-cols-2 bg-[#1E3A8A] text-white">
        <div className="px-2 py-2.5 text-sm font-semibold text-center uppercase tracking-wide">Samedi</div>
        <div className="px-2 py-2.5 text-sm font-semibold text-center uppercase tracking-wide">Dimanche</div>
      </div>
      <div className="bg-[#FCFBF8]">
        {weekends.map(([s, u]) => (
          <div key={fmtDate(s)} className="grid grid-cols-2">
            {[s, u].map((d) => {
              const outside = d.getMonth() !== month;
              if (outside) {
                return <div key={fmtDate(d)} className="min-h-[110px] border border-[#EAE6DF] bg-[#F4F1EB]/50 p-2 text-xs text-[#c4c9d4]">{d.getDate()}</div>;
              }
              if (!inYear(d)) {
                return (
                  <div key={fmtDate(d)} className="min-h-[110px] border border-[#EAE6DF] bg-[#F4F1EB]/50 p-2">
                    <div className="text-sm font-semibold text-[#c4c9d4]">{d.getDate()}</div>
                    <div className="text-[10px] text-[#c4c9d4] mt-1">Hors période</div>
                  </div>
                );
              }
              const dow = (d.getDay() + 6) % 7;
              const vac = isVacation(d, vacations);
              const todays = vac ? [] : coursesByDay[dow];
              const dayMeetings = meetings.filter((m) => m.date === fmtDate(d));
              const isToday = fmtDate(d) === fmtDate(new Date());
              return (
                <div key={fmtDate(d)} className={`min-h-[110px] border border-[#EAE6DF] p-2 flex flex-col gap-1 overflow-hidden ${vac ? "bg-[#F4F1EB]/60" : ""} ${isToday ? "ring-2 ring-[#C86444] ring-inset" : ""}`}>
                  <div className={`text-sm font-semibold ${vac ? "text-[#a8aebd]" : isToday ? "text-[#C86444]" : "text-[#1A2547]"}`}>{d.getDate()}</div>
                  {vac && <div className="text-[10px] text-[#8a92a3] truncate">Vacances · {vac.name}</div>}
                  {!vac && dayMeetings.slice(0, 2).map((m) => (
                    <div key={m.id} className="text-[10px] px-1.5 py-0.5 rounded bg-[#FBE8DC] text-[#C86444] truncate font-medium" title={m.title}>
                      ⚐ {m.start_time?.slice(0, 5)} {m.title}
                    </div>
                  ))}
                  {!vac && todays.slice(0, dayMeetings.length > 0 ? 3 : 4).map((c) => {
                    const colors = MAT_COLORS[c.matiere] || MAT_COLORS.arabe;
                    return (
                      <div key={c.id} className={`text-[10px] px-1.5 py-0.5 rounded ${colors.bg} ${colors.text} truncate`}>
                        {c.schedule_time?.split(" ")[0] || ""} {ML[c.matiere]}
                      </div>
                    );
                  })}
                  {!vac && (todays.length + dayMeetings.length) > 4 && <div className="text-[10px] text-[#8a92a3]">+{todays.length + dayMeetings.length - 4}</div>}
                </div>
              );
            })}
          </div>
        ))}
      </div>
    </div>
  );
}
